import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  ApiKeySummary,
  AdminUserSummary,
  ApplicationSettings,
  OrderSummary,
  PlaceOrderRequest,
  PortfolioHistoryPoint,
  PositionSummary,
  PredictionMarket,
  SuggestedMarket,
  UpdateApiKeyRequest,
  UserSummary,
} from "@papertiger/contracts";
import { api, type PortfolioResponse, type WalletListItem } from "./api.js";
import { ApiDocs } from "./ApiDocs.js";
import { Backtester } from "./Backtester.js";
import { useAutomaticRefreshActivity } from "./automatic-refresh.js";
import {
  PublicSite,
  resolvePublicRoute,
  useGoogleTagManager,
} from "./PublicSite.js";
import {
  DASHBOARD_NAVIGATION,
  getDashboardSectionLabel,
  getDashboardSectionPath,
  resolveDashboardSection,
  type DashboardSection,
} from "./dashboard-routing.js";
import {
  createEquityChartGeometry,
  createSmoothedEquityChartPath,
  getEquityChartRangeStart,
  type EquityChartRange,
} from "./equity-chart.js";
import {
  DEFAULT_PORTFOLIO_REFRESH_INTERVAL_SECONDS,
  MAXIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS,
  MINIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS,
  formatRefreshInterval,
  formatUpdatedAgo,
  readDashboardPreferences,
  validatePortfolioRefreshIntervalSeconds,
  writeDashboardPreferences,
} from "./live-preferences.js";
import {
  DEFAULT_CLOSED_POSITION_FILTERS,
  filterClosedPositions,
  getClosedPositionFilterOptions,
  serializeClosedPositionPrice,
  type ClosedPositionFilters,
} from "./position-filtering.js";
import {
  advancePositionSort,
  getPositionDisplayUnits,
  sortPositions,
  type PositionSort,
  type PositionSortKey,
} from "./position-sorting.js";
import { calculateDashboardMetrics } from "./dashboard-metrics.js";

/** Fixed refresh interval for the trending-market discovery list. */
const TRENDING_MARKET_REFRESH_INTERVAL_MILLISECONDS = 15_000;

/** Fast refresh interval while a user is actively evaluating a trade. */
const ACTIVE_MARKET_REFRESH_INTERVAL_MILLISECONDS = 1_000;

/** Minimum interval before repeating the same non-fatal venue warning. */
const VENUE_WARNING_REPEAT_INTERVAL_MILLISECONDS = 5 * 60_000;

/** Maximum execution-derived equity observations rendered by the dashboard chart. */
const EQUITY_HISTORY_POINT_LIMIT = 2_000;

/** Column labels for the unified position CSV export. */
const POSITIONS_CSV_HEADERS = [
  "Market",
  "Venue",
  "Contract",
  "Price",
  "Units",
  "Order time",
  "Order type",
  "Fill time",
  "Status",
  "Mark",
  "Unrealized P&L",
  "Settlement",
  "Realized P&L",
];

/** Column labels for the order CSV export. */
const ORDERS_CSV_HEADERS = [
  "Market",
  "Venue",
  "Contract",
  "Side",
  "Order type",
  "Time in force",
  "Status",
  "Filled units",
  "Requested units",
  "Average fill price",
  "Limit price",
  "Fee",
  "Gross",
  "Created at",
  "Updated at",
  "Client order ID",
];

/** A spreadsheet cell value. */
type CsvCell = string | number | null;

/** One spreadsheet row. */
type CsvRow = readonly CsvCell[];

/** Cached execution-derived equity observations grouped by date range for one wallet. */
type EquityHistoryByRange = Partial<
  Record<EquityChartRange, PortfolioHistoryPoint[]>
>;

/** Loaded execution-derived equity state associated with one wallet and range. */
interface LoadedEquityHistory {
  /** Wallet-and-range cache key. */
  cacheKey: string;
  /** Chronological persisted observations. */
  points: PortfolioHistoryPoint[];
}

/** Pending trade ticket selected from search or a position. */
interface TradeTicket {
  /** Venue. */
  venue: "polymarket" | "kalshi";
  /** Market identifier. */
  marketId: string;
  /** Outcome identifier. */
  outcomeId: string;
  /** Market title. */
  marketTitle: string;
  /** Outcome label. */
  outcomeName: string;
  /** External venue URL. */
  marketUrl: string;
  /** Buy or sell direction. */
  side: "buy" | "sell";
  /** Maximum owned quantity for sells. */
  maxQuantity?: number;
  /** Current indicative or liquidation price. */
  referencePricePercent1?: number | null;
}

/**
 * Root application component.
 *
 * @returns Authentication or trading interface.
 */
export function App(): React.JSX.Element {
  useGoogleTagManager();
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  const isApiDocs = pathname === "/docs";
  const publicRoute = resolvePublicRoute(pathname);
  const [user, setUser] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(api.hasToken() && !isApiDocs);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (isApiDocs) return;
    const verificationToken = new URLSearchParams(window.location.search).get("token");
    if (pathname === "/verify" && verificationToken !== null) {
      setLoading(true);
      api
        .confirmEmail(verificationToken)
        .then((confirmed) => {
          window.history.replaceState({}, "", "/");
          setUser(confirmed);
          setNotice("Email confirmed. Your paper wallet is ready.");
        })
        .catch((error: unknown) => {
          setNotice(readError(error));
        })
        .finally(() => setLoading(false));
      return;
    }
    if (api.hasToken()) {
      api
        .me()
        .then(setUser)
        .catch(() => api.setToken(null))
        .finally(() => setLoading(false));
    }
  }, [isApiDocs, pathname]);

  if (isApiDocs) {
    return <ApiDocs />;
  }
  if (publicRoute !== null && publicRoute !== "home") {
    return <PublicSite route={publicRoute} />;
  }
  if (loading) {
    return <div className="app-loader">Loading PaperTiger…</div>;
  }
  if (user === null) {
    return <Welcome notice={notice} onAuthenticated={setUser} />;
  }
  return (
    <Dashboard
      user={user}
      initialNotice={notice}
      onLogout={() => {
        setUser(null);
        setNotice(null);
      }}
    />
  );
}

/**
 * Public welcome and authentication view.
 *
 * @param props Notice and successful-auth callback.
 * @returns Welcome screen.
 */
function Welcome(props: {
  notice: string | null;
  onAuthenticated: (user: UserSummary) => void;
}): React.JSX.Element {
  const [mode, setMode] = useState<"register" | "login">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [eligibilityAcknowledged, setEligibilityAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(props.notice);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      if (mode === "register") {
        if (!eligibilityAcknowledged) {
          setMessage(
            "Confirm the 13+ and parental-permission requirements to create an account.",
          );
          return;
        }
        const response = await api.register(email, password, eligibilityAcknowledged);
        setMessage(response.message);
      } else {
        props.onAuthenticated(await api.login(email, password));
      }
    } catch (error) {
      setMessage(readError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicSite
      route="home"
      accountCard={
      <section className="auth-card surface">
        <div className="auth-tabs" role="tablist">
          <button
            className={mode === "register" ? "active" : ""}
            onClick={() => setMode("register")}
            type="button"
          >
            Create account
          </button>
          <button
            className={mode === "login" ? "active" : ""}
            onClick={() => setMode("login")}
            type="button"
          >
            Sign in
          </button>
        </div>
        <h2>{mode === "register" ? "Create account" : "Sign in"}</h2>
        <p className="muted">
          {mode === "register"
            ? "Creates one virtual wallet with a $10,000 starting balance. Email confirmation is required."
            : "Enter your PaperTiger account credentials."}
        </p>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              minLength={12}
              required
            />
          </label>
          {mode === "register" && (
            <label className="eligibility-acknowledgement">
              <input
                type="checkbox"
                checked={eligibilityAcknowledged}
                onChange={(event) => setEligibilityAcknowledged(event.target.checked)}
                required
              />
              <span>
                I confirm I am at least 13. If I am under the age of majority
                where I live, my parent or legal guardian permits me to use
                PaperTiger.
              </span>
            </label>
          )}
          <button
            className="primary wide"
            disabled={busy || (mode === "register" && !eligibilityAcknowledged)}
          >
            {busy ? "Working…" : mode === "register" ? "Create account" : "Sign in"}
          </button>
        </form>
        {mode === "register" && (
          <p className="auth-legal">
            By creating an account, you agree to the <a href="/terms">Terms</a>,
            acknowledge the <a href="/privacy">Privacy Policy</a>, and understand
            the <a href="/disclaimer">simulation disclaimer</a>. PaperTiger
            cannot place real-money trades.
          </p>
        )}
        {message !== null && <div className="notice">{message}</div>}
      </section>
      }
    />
  );
}

/**
 * Shared PaperTiger graphic mark.
 *
 * @param props Optional CSS class name.
 * @returns Responsive tiger-head logo.
 */
function BrandMark(props: { className?: string }): React.JSX.Element {
  return (
    <img
      className={props.className ?? "brand-mark"}
      src="/brand/papertiger-mark.png"
      alt="PaperTiger tiger logo"
      width="80"
      height="80"
    />
  );
}

/**
 * Authenticated trading dashboard.
 *
 * @param props User, notice, and logout callback.
 * @returns Dashboard.
 */
function Dashboard(props: {
  user: UserSummary;
  initialNotice: string | null;
  onLogout: () => void;
}): React.JSX.Element {
  const initialPreferences = useMemo(
    () => readDashboardPreferences(props.user.id),
    [props.user.id],
  );
  const preferencesRef = useRef(initialPreferences);
  const [wallets, setWallets] = useState<WalletListItem[]>([]);
  const [applicationSettings, setApplicationSettings] =
    useState<ApplicationSettings | null>(null);
  const [walletId, setWalletId] = useState<string | null>(
    initialPreferences.walletId,
  );
  const [
    portfolioRefreshIntervalSeconds,
    setPortfolioRefreshIntervalSeconds,
  ] = useState(initialPreferences.portfolioRefreshIntervalSeconds);
  const [portfolio, setPortfolio] = useState<PortfolioResponse | null>(null);
  const [portfolioUpdatedAtMilliseconds, setPortfolioUpdatedAtMilliseconds] =
    useState<number | null>(null);
  const [section, setSection] = useState<DashboardSection>(
    () => {
      const requestedSection =
        resolveDashboardSection(window.location.pathname) ?? "dashboard";
      return requestedSection === "admin" && !props.user.isAdmin
        ? "settings"
        : requestedSection;
    },
  );
  const [visitedSections, setVisitedSections] = useState<
    ReadonlySet<DashboardSection>
  >(() => new Set([section]));
  const [notice, setNotice] = useState(props.initialNotice);
  const [ticket, setTicket] = useState<TradeTicket | null>(null);
  const automaticRefreshActive = useAutomaticRefreshActivity();

  const showSection = useCallback((nextSection: DashboardSection): void => {
    setSection(nextSection);
    setVisitedSections((current) => {
      if (current.has(nextSection)) return current;
      return new Set([...current, nextSection]);
    });
    document.title = `${getDashboardSectionLabel(nextSection)} | PaperTiger`;
  }, []);

  const navigateToSection = useCallback(
    (nextSection: DashboardSection): void => {
      const nextPath = getDashboardSectionPath(nextSection);
      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
      showSection(nextSection);
    },
    [showSection],
  );

  useEffect(() => {
    const initialPath = getDashboardSectionPath(section);
    if (window.location.pathname !== initialPath) {
      window.history.replaceState({}, "", initialPath);
    }
    document.title = `${getDashboardSectionLabel(section)} | PaperTiger`;
    const handlePopState = (): void => {
      const nextSection = resolveDashboardSection(window.location.pathname);
      if (nextSection !== null) {
        showSection(
          nextSection === "admin" && !props.user.isAdmin
            ? "settings"
            : nextSection,
        );
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [section, showSection]);

  const refreshWallets = useCallback(async (): Promise<void> => {
    const nextWallets = await api.wallets();
    setWallets(nextWallets);
    setWalletId((current) => {
      const nextWalletId =
        nextWallets.some((wallet) => wallet.id === current)
          ? current
          : nextWallets[0]?.id ?? null;
      if (nextWalletId !== current) {
        preferencesRef.current = {
          ...preferencesRef.current,
          walletId: nextWalletId,
        };
        writeDashboardPreferences(props.user.id, preferencesRef.current);
      }
      return nextWalletId;
    });
  }, [props.user.id]);

  const refreshApplicationSettings = useCallback(async (): Promise<void> => {
    if (!props.user.isAdmin) return;
    const nextSettings = await api.applicationSettings();
    setApplicationSettings(nextSettings);
  }, [props.user.isAdmin]);

  const updateApplicationSettings = useCallback(
    async (nextSettings: ApplicationSettings): Promise<void> => {
      setApplicationSettings(await api.updateApplicationSettings(nextSettings));
    },
    [],
  );

  const selectWallet = useCallback((nextWalletId: string): void => {
    preferencesRef.current = {
      ...preferencesRef.current,
      walletId: nextWalletId,
    };
    writeDashboardPreferences(props.user.id, preferencesRef.current);
    setWalletId(nextWalletId);
  }, [props.user.id]);

  const updatePortfolioRefreshInterval = useCallback(
    (nextIntervalSeconds: number): void => {
      const validatedIntervalSeconds =
        validatePortfolioRefreshIntervalSeconds(nextIntervalSeconds);
      preferencesRef.current = {
        ...preferencesRef.current,
        portfolioRefreshIntervalSeconds: validatedIntervalSeconds,
      };
      writeDashboardPreferences(props.user.id, preferencesRef.current);
      setPortfolioRefreshIntervalSeconds(validatedIntervalSeconds);
    },
    [props.user.id],
  );

  const refreshPortfolio = useCallback(async (): Promise<void> => {
    if (walletId === null) {
      setPortfolio(null);
      setPortfolioUpdatedAtMilliseconds(null);
      return;
    }
    try {
      const nextPortfolio = await api.portfolio(walletId);
      setPortfolio(nextPortfolio);
      setPortfolioUpdatedAtMilliseconds(Date.now());
    } catch (error) {
      setNotice(readError(error));
    }
  }, [walletId]);

  useEffect(() => {
    void refreshWallets();
  }, [refreshWallets]);

  useEffect(() => {
    void refreshApplicationSettings().catch((error: unknown) => {
      setNotice(readError(error));
    });
  }, [refreshApplicationSettings]);

  useEffect(() => {
    if (!automaticRefreshActive) return;
    void refreshPortfolio();
    const interval = window.setInterval(
      () => void refreshPortfolio(),
      portfolioRefreshIntervalSeconds * 1_000,
    );
    return () => {
      window.clearInterval(interval);
    };
  }, [
    automaticRefreshActive,
    portfolioRefreshIntervalSeconds,
    refreshPortfolio,
  ]);

  const activeWallet = wallets.find((wallet) => wallet.id === walletId) ?? null;

  return (
    <div className="dashboard-shell">
      <aside className="sidebar">
        <div className="wordmark">
          <BrandMark className="sidebar-brand-mark" />
          PaperTiger
        </div>
        <nav aria-label="Application navigation">
          <div className="sidebar-primary-nav">
            {DASHBOARD_NAVIGATION.filter(
              (item) => item.section !== "admin" || props.user.isAdmin,
            ).map((item) => (
              <a
                aria-current={section === item.section ? "page" : undefined}
                className={section === item.section ? "active" : ""}
                href={item.path}
                key={item.section}
                onClick={(event) => {
                  event.preventDefault();
                  navigateToSection(item.section);
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="sidebar-resource-nav">
            <span className="sidebar-section-label">Resources</span>
            <a className="sidebar-link" data-short-label="AI" href="/integrations">AI integrations</a>
            <a className="sidebar-link" data-short-label="Docs" href="/docs">API documentation</a>
            <a className="sidebar-link" data-short-label="Support" href="/support">Support</a>
            <a className="sidebar-link" data-short-label="About" href="/about">About</a>
          </div>
        </nav>
        <div className="sidebar-bottom">
          <div className="sidebar-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="/disclaimer">Disclaimer</a>
          </div>
          <span>{props.user.email}</span>
          <button
            className="text-button"
            onClick={() => void api.logout().finally(props.onLogout)}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="dashboard">
        <header className="dashboard-header">
          <div className="wallet-picker">
            <select
              value={walletId ?? ""}
              onChange={(event) => selectWallet(event.target.value)}
              aria-label="Active wallet"
            >
              {wallets.map((wallet) => (
                <option key={wallet.id} value={wallet.id}>{wallet.name}</option>
              ))}
            </select>
            <span>Paper wallet</span>
          </div>
          <WalletActions
            wallet={activeWallet}
            walletCount={wallets.length}
            walletLimit={props.user.walletLimit}
            onChanged={() => void refreshWallets().then(refreshPortfolio)}
            onNotice={setNotice}
          />
        </header>
        {notice !== null && (
          <div className="notice dismissible">
            {notice}
            <button onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        <Metrics
          portfolio={portfolio}
          updatedAtMilliseconds={portfolioUpdatedAtMilliseconds}
        />
        {visitedSections.has("dashboard") && (
          <div
            className="cached-page"
            data-page-section="dashboard"
            hidden={section !== "dashboard"}
          >
            {activeWallet !== null && (
              <>
                <EquityChart
                  walletId={activeWallet.id}
                  currentPortfolio={portfolio}
                />
                <MarketSearch
                  walletId={activeWallet.id}
                  automaticRefreshActive={
                    automaticRefreshActive && section === "dashboard"
                  }
                  onTrade={setTicket}
                  onNotice={setNotice}
                />
              </>
            )}
          </div>
        )}
        {visitedSections.has("positions") && (
          <div
            className="cached-page"
            data-page-section="positions"
            hidden={section !== "positions"}
          >
            <Positions
              positions={portfolio?.positions ?? []}
              onSell={setTicket}
            />
          </div>
        )}
        {visitedSections.has("orders") && (
          <div
            className="cached-page"
            data-page-section="orders"
            hidden={section !== "orders"}
          >
            {walletId !== null && (
              <Orders
                walletId={walletId}
                automaticRefreshActive={automaticRefreshActive}
                onNotice={setNotice}
                onChanged={() => void refreshPortfolio()}
              />
            )}
          </div>
        )}
        {visitedSections.has("backtests") && (
          <div
            className="cached-page"
            data-page-section="backtests"
            hidden={section !== "backtests"}
          >
            <Backtester accessTier={props.user.accessTier} />
          </div>
        )}
        {visitedSections.has("api-keys") && (
          <div
            className="cached-page"
            data-page-section="api-keys"
            hidden={section !== "api-keys"}
          >
            <ApiKeys
              wallet={activeWallet}
              wallets={wallets}
              onNotice={setNotice}
            />
          </div>
        )}
        {visitedSections.has("settings") && (
          <div
            className="cached-page"
            data-page-section="settings"
            hidden={section !== "settings"}
          >
            <Settings
              email={props.user.email}
              isAdmin={props.user.isAdmin}
              accessTier={props.user.accessTier}
              trialEndsAt={props.user.trialEndsAt}
              portfolioRefreshIntervalSeconds={
                portfolioRefreshIntervalSeconds
              }
              onPortfolioRefreshIntervalChanged={
                updatePortfolioRefreshInterval
              }
              onAccountDeleted={props.onLogout}
            />
          </div>
        )}
        {props.user.isAdmin && visitedSections.has("admin") && (
          <div
            className="cached-page"
            data-page-section="admin"
            hidden={section !== "admin"}
          >
            <Administration
              applicationSettings={applicationSettings}
              onApplicationSettingsChanged={updateApplicationSettings}
              currentUserId={props.user.id}
            />
          </div>
        )}
      </main>
      {ticket !== null && activeWallet !== null && (
        <TradeDialog
          ticket={ticket}
          walletId={activeWallet.id}
          automaticRefreshActive={automaticRefreshActive}
          onClose={() => setTicket(null)}
          onFilled={(message) => {
            setTicket(null);
            setNotice(message);
            void refreshPortfolio();
          }}
        />
      )}
    </div>
  );
}

/**
 * Mark-to-market metric cards.
 *
 * @param props Current portfolio and successful refresh time.
 * @returns Metric row.
 */
function Metrics(props: {
  portfolio: PortfolioResponse | null;
  updatedAtMilliseconds: number | null;
}): React.JSX.Element {
  const wallet = props.portfolio?.wallet;
  const performance = wallet?.performance;
  const dashboardMetrics = calculateDashboardMetrics(
    wallet,
    props.portfolio?.positions ?? [],
  );
  return (
    <div className="metrics-block">
      <section className="metrics">
        <Metric label="Equity" value={formatMoney(wallet?.equityDollars)} tone="lavender" />
        <Metric label="Buying power" value={formatMoney(wallet?.buyingPowerDollars)} tone="mint" />
        <Metric
          label="Current positions"
          value={dashboardMetrics.currentPositionCount.toLocaleString("en-US")}
          detail="Open or awaiting settlement"
          tone="blue"
        />
        <Metric
          label="Total profit"
          value={formatSignedMoney(wallet?.totalProfitDollars)}
          detail={`${formatSignedPercent(performance?.totalProfitPercent100 ?? null)} · Avg ${formatSignedMoney(dashboardMetrics.averageProfitPerPositionDollars ?? undefined)} / position`}
          tone={(wallet?.totalProfitDollars ?? 0) >= 0 ? "mint" : "coral"}
        />
        <Metric
          label="Daily growth rate"
          value={formatSignedPercent(
            performance?.dailyGrowthRatePercent100 ?? null,
          )}
          detail={
            performance?.annualizedGrowthRatePercent100 === null ||
            performance === undefined
              ? "Annualized rate available after 90 days"
              : `Annualized: ${formatSignedPercent(performance.annualizedGrowthRatePercent100)} · 365-day year`
          }
          tone={(performance?.dailyGrowthRatePercent100 ?? 0) >= 0 ? "mint" : "coral"}
        />
        <Metric
          label="Max drawdown"
          value={formatDrawdownPercent(
            performance?.maxDrawdownPercent100,
          )}
          detail={`${formatDrawdownMoney(performance?.maxDrawdownDollars)} peak to trough`}
          tone={(performance?.maxDrawdownDollars ?? 0) === 0 ? "mint" : "coral"}
        />
        <Metric
          label="Sharpe ratio"
          value={formatRatio(performance?.sharpeRatio ?? null)}
          detail={
            performance?.sharpeRatio === null || performance === undefined
              ? "Available after 30 daily returns"
              : "Annualized from daily returns"
          }
          tone="lavender"
        />
        <Metric
          label="Win rate"
          value={formatPercent(dashboardMetrics.winRatePercent100)}
          detail={
            dashboardMetrics.winRatePercent100 === null
              ? "No closed wins or losses yet"
              : `${dashboardMetrics.winningPositionCount} wins · ${dashboardMetrics.losingPositionCount} losses`
          }
          tone="yellow"
        />
      </section>
      <RelativeUpdatedAt
        updatedAtMilliseconds={props.updatedAtMilliseconds}
      />
    </div>
  );
}

/**
 * Cached wallet equity chart replayed from immutable trades and settlements.
 *
 * @param props Active wallet and current marked portfolio.
 * @returns Selectable equity history chart.
 */
function EquityChart(props: {
  walletId: string;
  currentPortfolio: PortfolioResponse | null;
}): React.JSX.Element {
  const [range, setRange] = useState<EquityChartRange>("1d");
  const historyCacheRef = useRef<Map<string, EquityHistoryByRange>>(new Map());
  const cacheKey = `${props.walletId}:${range}`;
  const cachedPoints =
    historyCacheRef.current.get(props.walletId)?.[range] ?? [];
  const [loadedHistory, setLoadedHistory] = useState<LoadedEquityHistory>({
    cacheKey,
    points: cachedPoints,
  });
  const [updatedAtMilliseconds, setUpdatedAtMilliseconds] = useState<
    number | null
  >(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const tradeCashFingerprint =
    props.currentPortfolio?.wallet.id === props.walletId
      ? `${props.currentPortfolio.wallet.cashBalanceDollars}`
      : "";

  useEffect(() => {
    const nextCachedPoints =
      historyCacheRef.current.get(props.walletId)?.[range] ?? [];
    setLoadedHistory({ cacheKey, points: nextCachedPoints });
    let active = true;
    const refreshHistory = async (): Promise<void> => {
      try {
        const start = getEquityChartRangeStart(range, Date.now());
        const points = await api.portfolioHistory(props.walletId, {
          ...(start === undefined ? {} : { start }),
          limit: EQUITY_HISTORY_POINT_LIMIT,
        });
        if (!active) return;
        const walletHistory =
          historyCacheRef.current.get(props.walletId) ?? {};
        historyCacheRef.current.set(props.walletId, {
          ...walletHistory,
          [range]: points,
        });
        setLoadedHistory({ cacheKey, points });
        setUpdatedAtMilliseconds(Date.now());
        setLoadError(null);
      } catch (error) {
        if (active) setLoadError(readError(error));
      }
    };
    void refreshHistory();
    return () => {
      active = false;
    };
  }, [props.walletId, range, cacheKey, tradeCashFingerprint]);

  const persistedPoints =
    loadedHistory.cacheKey === cacheKey ? loadedHistory.points : cachedPoints;
  const points = persistedPoints;

  const width = 1_000;
  const height = 280;
  const padding = 20;
  const geometry = useMemo(
    () => createEquityChartGeometry(points, width, height, padding),
    [points],
  );
  const coordinateText =
    geometry?.coordinates
      .map((coordinate) => `${coordinate.x.toFixed(1)},${coordinate.y.toFixed(1)}`)
      .join(" ") ?? "";
  const smoothPath = createSmoothedEquityChartPath(geometry?.coordinates ?? []);
  const firstPoint = points[0];
  const lastPoint = points.at(-1);
  const changeDollars =
    firstPoint === undefined || lastPoint === undefined
      ? undefined
      : lastPoint.equityDollars - firstPoint.equityDollars;
  const lineTone =
    changeDollars === undefined || changeDollars >= 0 ? "positive" : "negative";
  const firstCoordinate = geometry?.coordinates[0];
  const lastCoordinate = geometry?.coordinates.at(-1);
  const areaPoints =
    firstCoordinate === undefined || lastCoordinate === undefined
      ? ""
      : [
          `${firstCoordinate.x.toFixed(1)},${height - padding}`,
          coordinateText,
          `${lastCoordinate.x.toFixed(1)},${height - padding}`,
        ].join(" ");

  return (
    <section className="equity-chart-card surface">
      <div className="equity-chart-heading">
        <div>
          <p className="eyebrow">Performance</p>
          <h2>Trade equity</h2>
        </div>
        <div className="equity-range-picker" aria-label="Equity date range">
          {(["1d", "1w", "1m", "all"] as const).map((option) => (
            <button
              aria-pressed={range === option}
              className={range === option ? "active" : ""}
              key={option}
              onClick={() => setRange(option)}
              type="button"
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="equity-chart-summary">
        <strong>{formatMoney(lastPoint?.equityDollars)}</strong>
        <span className={lineTone}>
          {changeDollars === undefined
            ? "—"
            : `${formatSignedMoney(changeDollars)} in range`}
        </span>
        <RelativeUpdatedAt
          className="updated-at"
          updatedAtMilliseconds={updatedAtMilliseconds}
        />
      </div>
      {geometry === null ? (
        <div className="equity-chart-empty">Loading equity history…</div>
      ) : (
        <>
          <div className="equity-chart-plot">
            <span className="equity-axis maximum">
              {formatMoney(geometry.maximumEquityDollars)}
            </span>
            <svg
              aria-label={`Equity from ${formatDateTime(firstPoint?.timestamp ?? "")} to ${formatDateTime(lastPoint?.timestamp ?? "")}`}
              preserveAspectRatio="none"
              role="img"
              viewBox={`0 0 ${width} ${height}`}
            >
              <line className="equity-grid-line" x1={padding} x2={width - padding} y1={padding} y2={padding} />
              <line className="equity-grid-line" x1={padding} x2={width - padding} y1={height / 2} y2={height / 2} />
              <line className="equity-grid-line" x1={padding} x2={width - padding} y1={height - padding} y2={height - padding} />
              <polygon className={`equity-area ${lineTone}`} points={areaPoints} />
              <path className={`equity-line ${lineTone}`} d={smoothPath} />
              {geometry.coordinates.length === 1 && (
                <circle
                  className={`equity-point ${lineTone}`}
                  cx={geometry.coordinates[0]?.x}
                  cy={geometry.coordinates[0]?.y}
                  r="5"
                />
              )}
            </svg>
            <span className="equity-axis minimum">
              {formatMoney(geometry.minimumEquityDollars)}
            </span>
          </div>
          <div className="equity-chart-dates">
            <span>{firstPoint === undefined ? "—" : formatDateTime(firstPoint.timestamp)}</span>
            <span>{lastPoint === undefined ? "—" : formatDateTime(lastPoint.timestamp)}</span>
          </div>
        </>
      )}
      {loadError !== null && (
        <div className="equity-chart-error" role="status">
          Showing cached equity. {loadError}
        </div>
      )}
    </section>
  );
}

/**
 * One equity metric.
 *
 * @param props Label, formatted value, and color.
 * @returns Metric card.
 */
function Metric(props: {
  label: string;
  value: string;
  detail?: string;
  tone: string;
}): React.JSX.Element {
  return (
    <div className={`metric surface ${props.tone}`}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
      {props.detail !== undefined && <small>{props.detail}</small>}
    </div>
  );
}

/**
 * Live relative timestamp that advances without triggering data requests.
 *
 * @param props Successful refresh timestamp and optional CSS class.
 * @returns Relative update status.
 */
function RelativeUpdatedAt(props: {
  updatedAtMilliseconds: number | null;
  className?: string;
}): React.JSX.Element {
  const [nowMilliseconds, setNowMilliseconds] = useState(Date.now());
  useEffect(() => {
    const interval = window.setInterval(
      () => setNowMilliseconds(Date.now()),
      1_000,
    );
    return () => window.clearInterval(interval);
  }, []);
  return (
    <span className={props.className ?? "updated-at"} aria-live="polite">
      {formatUpdatedAgo(props.updatedAtMilliseconds, nowMilliseconds)}
    </span>
  );
}

/**
 * User-scoped live dashboard settings.
 *
 * @param props Current interval and persistence callback.
 * @returns Settings page.
 */
function Settings(props: {
  email: string;
  isAdmin: boolean;
  accessTier: UserSummary["accessTier"];
  trialEndsAt: string | null;
  portfolioRefreshIntervalSeconds: number;
  onPortfolioRefreshIntervalChanged: (intervalSeconds: number) => void;
  onAccountDeleted: () => void;
}): React.JSX.Element {
  const [intervalSeconds, setIntervalSeconds] = useState(
    props.portfolioRefreshIntervalSeconds,
  );
  const [billingError, setBillingError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [accountError, setAccountError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  useEffect(() => {
    setIntervalSeconds(props.portfolioRefreshIntervalSeconds);
  }, [props.portfolioRefreshIntervalSeconds]);
  const save = (event: FormEvent): void => {
    event.preventDefault();
    props.onPortfolioRefreshIntervalChanged(intervalSeconds);
  };
  const changePassword = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSavingPassword(true);
    setAccountError(null);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setAccountError("Password updated.");
    } catch (error) {
      setAccountError(readError(error));
    } finally {
      setSavingPassword(false);
    }
  };
  const deleteAccount = async (): Promise<void> => {
    if (!window.confirm("Delete your account and every wallet, order, position, API key, and saved record? This cannot be undone.")) return;
    setDeletingAccount(true);
    setAccountError(null);
    try {
      await api.deleteAccount(currentPassword);
      props.onAccountDeleted();
    } catch (error) {
      setAccountError(readError(error));
    } finally {
      setDeletingAccount(false);
    }
  };
  return (
    <section>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Preferences</p>
          <h2>Settings</h2>
        </div>
      </div>
      <div className="settings-stack">
        <form className="settings-card surface" onSubmit={save}>
          <label>
            Portfolio refresh interval (seconds)
            <input
              type="number"
              min={MINIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS}
              max={MAXIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS}
              step="1"
              value={intervalSeconds}
              onChange={(event) => setIntervalSeconds(Number(event.target.value))}
              required
            />
          </label>
          <p className="muted">
            Equity, buying power, position value, and profit currently refresh
            every {formatRefreshInterval(props.portfolioRefreshIntervalSeconds)}.
            The setting is saved in this browser for your account.
          </p>
          <div className="header-actions">
            <button className="primary" type="submit">Save refresh interval</button>
            <button
              className="secondary"
              type="button"
              onClick={() => {
                setIntervalSeconds(DEFAULT_PORTFOLIO_REFRESH_INTERVAL_SECONDS);
                props.onPortfolioRefreshIntervalChanged(
                  DEFAULT_PORTFOLIO_REFRESH_INTERVAL_SECONDS,
                );
              }}
            >
              Use 15-second default
            </button>
          </div>
        </form>
        <form className="settings-card surface" onSubmit={(event) => void changePassword(event)}>
          <p className="eyebrow">Account</p>
          <h3>Sign-in details</h3>
          <p className="muted">Your email, <strong>{props.email}</strong>, cannot be changed yet.</p>
          <label>
            Current password
            <input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} minLength={12} maxLength={200} required />
          </label>
          <label>
            New password
            <input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} maxLength={200} required />
          </label>
          {accountError !== null && <p className={accountError === "Password updated." ? "muted" : "form-error"} role="alert">{accountError}</p>}
          <div className="header-actions">
            <button className="primary" type="submit" disabled={savingPassword || deletingAccount}>{savingPassword ? "Updating…" : "Update password"}</button>
            {!props.isAdmin && <button className="danger-text" type="button" disabled={deletingAccount || currentPassword.length < 12} onClick={() => void deleteAccount()}>{deletingAccount ? "Deleting…" : "Delete account"}</button>}
          </div>
          {!props.isAdmin && <p className="muted">Deleting your account permanently removes all PaperTiger data associated with it and signs you out.</p>}
        </form>
        <section className="settings-card surface">
          <p className="eyebrow">{props.isAdmin ? "Administrator" : "Billing"}</p>
          <h3>
            {props.isAdmin
              ? "Administrator account"
              : props.accessTier === "paid"
                ? "Paid account"
                : props.accessTier === "trial"
                  ? "Free trial"
                  : "Free account"}
          </h3>
          <p className="muted">
            {props.isAdmin
              ? "Your administrator role provides full product access and access to the separate Administration page."
              : `${props.accessTier === "trial" && props.trialEndsAt !== null ? `Your trial ends ${new Date(props.trialEndsAt).toLocaleDateString("en-US")}. ` : ""}Paid accounts can create up to 25 wallets. Backtests remain temporarily unavailable while full market data is in development.`}
          </p>
          {billingError !== null && <p className="form-error" role="alert">{billingError}</p>}
          <div className="header-actions">
            <a className="secondary link-button" href="/pricing">Compare plans</a>
            {!props.isAdmin && props.accessTier === "paid" ? (
              <button className="secondary" type="button" onClick={() => void api.billingPortal().then((url) => { window.location.assign(url); }).catch((error: unknown) => setBillingError(readError(error)))}>Manage billing</button>
            ) : !props.isAdmin ? (
              <button className="primary" type="button" onClick={() => void api.startCheckout().then((url) => { window.location.assign(url); }).catch((error: unknown) => setBillingError(readError(error)))}>Upgrade to Paid</button>
            ) : null}
          </div>
        </section>
      </div>
    </section>
  );
}

/**
 * Administrator-only operational policy controls.
 *
 * @param props Current policy and persistence callback.
 * @returns Administration page.
 */
function Administration(props: {
  applicationSettings: ApplicationSettings | null;
  onApplicationSettingsChanged: (settings: ApplicationSettings) => Promise<void>;
  currentUserId: string;
}): React.JSX.Element {
  const [freeWalletLimit, setFreeWalletLimit] = useState(props.applicationSettings?.freeWalletLimit ?? 1);
  const [trialWalletLimit, setTrialWalletLimit] = useState(props.applicationSettings?.trialWalletLimit ?? 25);
  const [paidWalletLimit, setPaidWalletLimit] = useState(props.applicationSettings?.paidWalletLimit ?? 25);
  const [trialDurationDays, setTrialDurationDays] = useState(props.applicationSettings?.trialDurationDays ?? 7);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "admin" | "user">("all");
  const [tierFilter, setTierFilter] = useState<"all" | AdminUserSummary["accessTier"]>("all");
  const [sortKey, setSortKey] = useState<"email" | "accessTier" | "createdAt">("createdAt");
  const [sortDirection, setSortDirection] = useState<"ascending" | "descending">("descending");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "user">("user");
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<"admin" | "user">("user");
  const [editingPassword, setEditingPassword] = useState("");
  useEffect(() => {
    if (props.applicationSettings === null) return;
    setFreeWalletLimit(props.applicationSettings.freeWalletLimit);
    setTrialWalletLimit(props.applicationSettings.trialWalletLimit);
    setPaidWalletLimit(props.applicationSettings.paidWalletLimit);
    setTrialDurationDays(props.applicationSettings.trialDurationDays);
  }, [props.applicationSettings]);
  const refreshUsers = async (): Promise<void> => {
    try {
      setUsers(await api.adminUsers());
      setUsersError(null);
    } catch (error) {
      setUsersError(readError(error));
    }
  };
  useEffect(() => { void refreshUsers(); }, []);
  const toggleSort = (key: typeof sortKey): void => {
    if (sortKey === key) {
      setSortDirection((current) => current === "ascending" ? "descending" : "ascending");
    } else {
      setSortKey(key);
      setSortDirection(key === "createdAt" ? "descending" : "ascending");
    }
  };
  const visibleUsers = users
    .filter((user) => user.email.includes(userSearch.trim().toLowerCase()))
    .filter((user) => roleFilter === "all" || user.role === roleFilter)
    .filter((user) => tierFilter === "all" || user.accessTier === tierFilter)
    .toSorted((left, right) => {
      const leftValue = sortKey === "createdAt" ? left.createdAt : left[sortKey];
      const rightValue = sortKey === "createdAt" ? right.createdAt : right[sortKey];
      const order = leftValue.localeCompare(rightValue);
      return sortDirection === "ascending" ? order : -order;
    });
  const createUser = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setCreatingUser(true);
    try {
      await api.createAdminUser({ email: newUserEmail, password: newUserPassword, role: newUserRole });
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserRole("user");
      await refreshUsers();
    } catch (error) {
      setUsersError(readError(error));
    } finally {
      setCreatingUser(false);
    }
  };
  const saveUser = async (userId: string): Promise<void> => {
    try {
      await api.updateAdminUser(userId, {
        role: editingRole,
        ...(editingPassword === "" ? {} : { password: editingPassword }),
      });
      setEditingUserId(null);
      setEditingPassword("");
      await refreshUsers();
    } catch (error) {
      setUsersError(readError(error));
    }
  };
  const deleteUser = async (user: AdminUserSummary): Promise<void> => {
    if (!window.confirm(`Delete ${user.email} and every record they own? This cannot be undone.`)) return;
    try {
      await api.deleteAdminUser(user.id);
      await refreshUsers();
    } catch (error) {
      setUsersError(readError(error));
    }
  };
  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await props.onApplicationSettingsChanged({
        freeWalletLimit,
        trialWalletLimit,
        paidWalletLimit,
        trialDurationDays,
      });
    } catch (saveError) {
      setError(readError(saveError));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Administrator</p>
          <h2>Administration</h2>
        </div>
      </div>
      <form className="settings-card surface" onSubmit={(event) => void save(event)}>
        <h3>Access policy</h3>
        <label>
          Free-account wallet limit
          <input type="number" min="1" max="1000" step="1" value={freeWalletLimit} onChange={(event) => setFreeWalletLimit(Number(event.target.value))} required />
        </label>
        <label>
          Trial-account wallet limit
          <input type="number" min="1" max="1000" step="1" value={trialWalletLimit} onChange={(event) => setTrialWalletLimit(Number(event.target.value))} required />
        </label>
        <label>
          Paid-account wallet limit
          <input type="number" min="1" max="1000" step="1" value={paidWalletLimit} onChange={(event) => setPaidWalletLimit(Number(event.target.value))} required />
        </label>
        <label>
          Trial length (days)
          <input type="number" min="1" max="365" step="1" value={trialDurationDays} onChange={(event) => setTrialDurationDays(Number(event.target.value))} required />
        </label>
        <p className="muted">
          Existing wallets are unchanged when a limit is lowered. Backtests remain unavailable while historical market data is paused.
        </p>
        {error !== null && <p className="form-error" role="alert">{error}</p>}
        <div className="header-actions">
          <button className="primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save access policy"}
          </button>
        </div>
      </form>
      <section className="admin-users surface">
        <div className="admin-users-heading">
          <div><p className="eyebrow">User management</p><h3>Users</h3><p className="muted">Create, search, filter, update, or permanently remove accounts.</p></div>
          <span className="user-count">{visibleUsers.length} shown</span>
        </div>
        <form className="admin-user-create" onSubmit={(event) => void createUser(event)}>
          <input type="email" aria-label="New user email" placeholder="email@example.com" value={newUserEmail} onChange={(event) => setNewUserEmail(event.target.value)} required />
          <input type="password" aria-label="New user password" placeholder="Temporary password" value={newUserPassword} onChange={(event) => setNewUserPassword(event.target.value)} minLength={12} maxLength={200} required />
          <select aria-label="New user role" value={newUserRole} onChange={(event) => setNewUserRole(event.target.value as "admin" | "user")}><option value="user">User</option><option value="admin">Administrator</option></select>
          <button className="primary" type="submit" disabled={creatingUser}>{creatingUser ? "Creating…" : "Add user"}</button>
        </form>
        <div className="admin-user-toolbar">
          <input type="search" aria-label="Search users" placeholder="Search email" value={userSearch} onChange={(event) => setUserSearch(event.target.value)} />
          <select aria-label="Filter users by role" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value as typeof roleFilter)}><option value="all">All roles</option><option value="admin">Administrators</option><option value="user">Users</option></select>
          <select aria-label="Filter users by access" value={tierFilter} onChange={(event) => setTierFilter(event.target.value as typeof tierFilter)}><option value="all">All access</option><option value="free">Free</option><option value="trial">Trial</option><option value="paid">Paid</option></select>
        </div>
        {usersError !== null && <p className="form-error" role="alert">{usersError}</p>}
        <div className="admin-user-table-wrap"><table className="admin-user-table"><thead><tr>
          <th><button type="button" onClick={() => toggleSort("email")}>Email</button></th><th>Role</th><th><button type="button" onClick={() => toggleSort("accessTier")}>Access</button></th><th>Verified</th><th><button type="button" onClick={() => toggleSort("createdAt")}>Joined</button></th><th><span className="sr-only">Actions</span></th>
        </tr></thead><tbody>{visibleUsers.map((user) => {
          const editing = editingUserId === user.id;
          const self = user.id === props.currentUserId;
          return <tr key={user.id}>
            <td><strong>{user.email}</strong>{self && <small>Current account</small>}</td>
            <td>{editing ? <select aria-label={`Role for ${user.email}`} value={editingRole} onChange={(event) => setEditingRole(event.target.value as "admin" | "user")}><option value="user">User</option><option value="admin">Administrator</option></select> : <span className="user-pill">{user.role}</span>}</td>
            <td><span className={`user-pill ${user.accessTier}`}>{user.accessTier}</span>{user.trialEndsAt !== null && <small>Ends {new Date(user.trialEndsAt).toLocaleDateString("en-US")}</small>}</td>
            <td>{user.emailVerified ? "Verified" : "Pending"}</td><td>{new Date(user.createdAt).toLocaleDateString("en-US")}</td>
            <td><div className="admin-user-actions">{editing ? <><input type="password" aria-label={`New password for ${user.email}`} placeholder="New password (optional)" value={editingPassword} onChange={(event) => setEditingPassword(event.target.value)} minLength={12} maxLength={200} /><button className="primary compact" type="button" onClick={() => void saveUser(user.id)}>Save</button><button className="secondary compact" type="button" onClick={() => setEditingUserId(null)}>Cancel</button></> : <><button className="secondary compact" type="button" disabled={self} onClick={() => { setEditingUserId(user.id); setEditingRole(user.role); setEditingPassword(""); }}>Edit</button><button className="danger-text" type="button" disabled={self} onClick={() => void deleteUser(user)}>Delete</button></>}</div></td>
          </tr>;
        })}</tbody></table></div>
      </section>
    </section>
  );
}

/**
 * Wallet creation and reset controls.
 *
 * @param props Wallet state and callbacks.
 * @returns Wallet controls.
 */
function WalletActions(props: {
  wallet: WalletListItem | null;
  walletCount: number;
  walletLimit: number | null;
  onChanged: () => void;
  onNotice: (message: string) => void;
}): React.JSX.Element {
  const create = async (): Promise<void> => {
    const name = window.prompt("Wallet name");
    if (name === null || name.trim() === "") return;
    const balance = Number(window.prompt("Starting paper balance", "10000"));
    if (!Number.isFinite(balance)) return;
    try {
      await api.createWallet(name, balance);
      props.onChanged();
    } catch (error) {
      props.onNotice(readError(error));
    }
  };
  const reset = async (): Promise<void> => {
    if (
      props.wallet === null ||
      !window.confirm(`Reset ${props.wallet.name}? All positions and orders will be removed.`)
    ) return;
    try {
      await api.resetWallet(props.wallet.id);
      props.onChanged();
      props.onNotice(`${props.wallet.name} was reset.`);
    } catch (error) {
      props.onNotice(readError(error));
    }
  };
  return (
    <div className="header-actions">
      <button
        className="secondary"
        onClick={() => void create()}
        disabled={props.walletLimit !== null && props.walletCount >= props.walletLimit}
      >
        New wallet
      </button>
      <button className="danger-text" onClick={() => void reset()} disabled={props.wallet === null}>Reset</button>
    </div>
  );
}

/**
 * Search and market selection surface.
 *
 * @param props Wallet and trade callbacks.
 * @returns Search section.
 */
function MarketSearch(props: {
  walletId: string;
  automaticRefreshActive: boolean;
  onTrade: (ticket: TradeTicket) => void;
  onNotice: (message: string) => void;
}): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [venue, setVenue] = useState<"all" | "polymarket" | "kalshi">("all");
  const [markets, setMarkets] = useState<PredictionMarket[]>([]);
  const [searching, setSearching] = useState(false);
  const [
    marketResultsUpdatedAtMilliseconds,
    setMarketResultsUpdatedAtMilliseconds,
  ] = useState<number | null>(null);
  const [marketUrl, setMarketUrl] = useState("");
  const [resolvedMarketUrl, setResolvedMarketUrl] = useState<string | null>(
    null,
  );
  const [resolvingUrl, setResolvingUrl] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedMarket[]>([]);
  const [
    suggestionsUpdatedAtMilliseconds,
    setSuggestionsUpdatedAtMilliseconds,
  ] = useState<number | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const searchRequestId = useRef(0);
  const venueWarningLastShownAtMilliseconds = useRef(new Map<string, number>());
  const showWarnings = useCallback((warnings: string[]): void => {
    const nowMilliseconds = Date.now();
    const newWarnings = warnings.filter((warning) => {
      const lastShownAtMilliseconds = venueWarningLastShownAtMilliseconds.current.get(warning);
      if (
        lastShownAtMilliseconds !== undefined &&
        nowMilliseconds - lastShownAtMilliseconds < VENUE_WARNING_REPEAT_INTERVAL_MILLISECONDS
      ) {
        return false;
      }
      venueWarningLastShownAtMilliseconds.current.set(warning, nowMilliseconds);
      return true;
    });
    if (newWarnings.length > 0) {
      props.onNotice(newWarnings.join(" "));
    }
  }, [props.onNotice]);

  useEffect(() => {
    if (!props.automaticRefreshActive) return;
    let active = true;
    let inFlight = false;
    const refreshSuggestions = async (): Promise<void> => {
      if (inFlight) {
        return;
      }
      inFlight = true;
      try {
        const response = await api.suggestedMarkets();
        if (!active) return;
        setSuggestions(response.markets);
        setSuggestionsUpdatedAtMilliseconds(Date.now());
        showWarnings(response.warnings);
      } catch (error) {
        if (active) props.onNotice(readError(error));
      } finally {
        inFlight = false;
        if (active) setLoadingSuggestions(false);
      }
    };
    void refreshSuggestions();
    const interval = window.setInterval(
      () => void refreshSuggestions(),
      TRENDING_MARKET_REFRESH_INTERVAL_MILLISECONDS,
    );
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [props.automaticRefreshActive, showWarnings]);

  useEffect(() => {
    const searchQuery = query.trim();
    if (!props.automaticRefreshActive || searchQuery.length < 2) {
      return;
    }
    let active = true;
    let inFlight = false;
    const refreshSearchResults = async (showSearching: boolean): Promise<void> => {
      if (inFlight) {
        return;
      }
      const requestId = ++searchRequestId.current;
      inFlight = true;
      if (showSearching) {
        setSearching(true);
      }
      try {
        const response = await api.searchMarkets(searchQuery, venue);
        if (!active || requestId !== searchRequestId.current) return;
        setMarkets(response.markets);
        setMarketResultsUpdatedAtMilliseconds(Date.now());
        showWarnings(response.warnings);
      } catch (error) {
        if (active && requestId === searchRequestId.current) {
          props.onNotice(readError(error));
        }
      } finally {
        inFlight = false;
        if (active && requestId === searchRequestId.current) {
          setSearching(false);
        }
      }
    };
    const initialDelay = window.setTimeout(
      () => void refreshSearchResults(true),
      350,
    );
    const interval = window.setInterval(
      () => void refreshSearchResults(false),
      ACTIVE_MARKET_REFRESH_INTERVAL_MILLISECONDS,
    );
    return () => {
      active = false;
      searchRequestId.current += 1;
      window.clearTimeout(initialDelay);
      window.clearInterval(interval);
    };
  }, [props.automaticRefreshActive, query, showWarnings, venue]);

  useEffect(() => {
    if (!props.automaticRefreshActive || resolvedMarketUrl === null) {
      return;
    }
    let active = true;
    let inFlight = false;
    const refreshResolvedMarket = async (): Promise<void> => {
      if (inFlight) {
        return;
      }
      inFlight = true;
      try {
        const response = await api.resolveMarketUrl(resolvedMarketUrl);
        if (!active) return;
        setMarkets(response.markets);
        setMarketResultsUpdatedAtMilliseconds(Date.now());
        showWarnings(response.warnings);
      } catch (error) {
        if (active) {
          props.onNotice(readError(error));
        }
      } finally {
        inFlight = false;
      }
    };
    const interval = window.setInterval(
      () => void refreshResolvedMarket(),
      ACTIVE_MARKET_REFRESH_INTERVAL_MILLISECONDS,
    );
    void refreshResolvedMarket();
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [
    props.automaticRefreshActive,
    resolvedMarketUrl,
    showWarnings,
  ]);

  const resolveUrl = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    searchRequestId.current += 1;
    setResolvingUrl(true);
    try {
      const response = await api.resolveMarketUrl(marketUrl);
      setMarkets(response.markets);
      setQuery("");
      setResolvedMarketUrl(marketUrl);
      setMarketResultsUpdatedAtMilliseconds(Date.now());
      showWarnings(response.warnings);
      if (response.markets.length > 0) {
        props.onNotice(`Loaded ${response.markets.length} market${response.markets.length === 1 ? "" : "s"} from the venue link.`);
      }
    } catch (error) {
      props.onNotice(readError(error));
    } finally {
      setResolvingUrl(false);
    }
  };

  return (
    <section>
      <div className="search-bar surface">
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setResolvedMarketUrl(null);
            if (event.target.value.trim().length < 2) {
              searchRequestId.current += 1;
              setMarkets([]);
              setMarketResultsUpdatedAtMilliseconds(null);
              setSearching(false);
            }
          }}
          placeholder="Search markets…"
          aria-label="Search markets"
        />
        <select value={venue} onChange={(event) => setVenue(event.target.value as typeof venue)} aria-label="Market venue">
          <option value="all">All venues</option>
          <option value="polymarket">Polymarket</option>
          <option value="kalshi">Kalshi</option>
        </select>
        <span className="search-status" aria-live="polite">{searching ? "Searching…" : ""}</span>
      </div>
      <form className="url-bar" onSubmit={(event) => void resolveUrl(event)}>
        <label>
          Or paste a market URL
          <input
            type="url"
            value={marketUrl}
            onChange={(event) => {
              setMarketUrl(event.target.value);
              setResolvedMarketUrl(null);
            }}
            placeholder="Paste a Polymarket or Kalshi market URL"
            required
          />
        </label>
        <button className="secondary" disabled={resolvingUrl}>
          {resolvingUrl ? "Loading…" : "Open market"}
        </button>
      </form>
      {(markets.length > 0 || (query.trim().length >= 2 && !searching)) && (
        <MarketSearchResults
          markets={markets}
          empty={query.trim().length >= 2 && markets.length === 0}
          updatedAtMilliseconds={marketResultsUpdatedAtMilliseconds}
          onTrade={props.onTrade}
        />
      )}
      <div className="suggested-markets surface">
        <div className="suggested-heading">
          <h2>Trending markets</h2>
          <RelativeUpdatedAt
            className="updated-at"
            updatedAtMilliseconds={suggestionsUpdatedAtMilliseconds}
          />
        </div>
        {loadingSuggestions ? (
          <div className="suggested-loading">Finding live opportunities…</div>
        ) : suggestions.length === 0 ? (
          <div className="suggested-loading">No actively traded markets are moving enough right now.</div>
        ) : (
          <div className="suggestion-list">
            {suggestions.map((market) => (
              <article className="suggestion" key={`${market.venue}:${market.marketId}`}>
                <div className="suggestion-copy">
                  <div className="market-meta">
                    <span className={`venue ${market.venue}`}>{market.venue}</span>
                    <span>{market.category ?? "Market"}</span>
                  </div>
                  <h4>{market.title}</h4>
                  <p>
                    <strong className={market.oddsChangePercent100 >= 0 ? "positive" : "negative"}>
                      {formatSignedPoints(market.oddsChangePercent100)}
                    </strong>
                    {" "}in 24h · {formatMoney(market.liquidityDollars)} liquidity
                    {market.closesAt === null ? "" : ` · closes ${formatDate(market.closesAt)}`}
                  </p>
                </div>
                <div className="suggestion-outcomes">
                  {market.outcomes.map((outcome) => (
                    <button
                      key={outcome.id}
                      className="secondary compact"
                      onClick={() => props.onTrade(
                        toBuyTicket(
                          market,
                          outcome.id,
                          outcome.name,
                          outcome.pricePercent1,
                        ),
                      )}
                    >
                      {outcome.name} {formatProbability(outcome.pricePercent1)}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Compact type-ahead result list for matching live venue markets.
 *
 * @param props Current matches, empty state, and trade callback.
 * @returns Search result dropdown.
 */
function MarketSearchResults(props: {
  markets: PredictionMarket[];
  empty: boolean;
  updatedAtMilliseconds: number | null;
  onTrade: (ticket: TradeTicket) => void;
}): React.JSX.Element {
  return (
    <div className="market-search-results surface">
      <div className="market-results-status">
        <span>Live market prices</span>
        <RelativeUpdatedAt
          updatedAtMilliseconds={props.updatedAtMilliseconds}
        />
      </div>
      {props.empty ? (
        <div className="search-empty">No active market matches.</div>
      ) : (
        props.markets.map((market) => (
          <article className="market-search-result" key={`${market.venue}:${market.marketId}`}>
            <div>
              <div className="market-meta">
                <span className={`venue ${market.venue}`}>{market.venue}</span>
                <span>{market.closesAt === null ? "Open" : formatDate(market.closesAt)}</span>
              </div>
              <strong>{market.title}</strong>
            </div>
            <div className="suggestion-outcomes">
              {market.outcomes.map((outcome) => (
                <button
                  key={outcome.id}
                  className="secondary compact"
                  onClick={() => props.onTrade(toBuyTicket(
                    market,
                    outcome.id,
                    outcome.name,
                    outcome.pricePercent1,
                  ))}
                >
                  {outcome.name} {formatProbability(outcome.pricePercent1)}
                </button>
              ))}
            </div>
          </article>
        ))
      )}
    </div>
  );
}

/**
 * Creates a buy ticket from a market outcome.
 *
 * @param market Normalized public venue market.
 * @param outcomeId Venue outcome identifier.
 * @param outcomeName Visible outcome label.
 * @param referencePricePercent1 Current indicative outcome price.
 * @returns Ready-to-trade buy ticket.
 */
function toBuyTicket(
  market: PredictionMarket,
  outcomeId: string,
  outcomeName: string,
  referencePricePercent1: number | null,
): TradeTicket {
  return {
    venue: market.venue,
    marketId: market.marketId,
    outcomeId,
    marketTitle: market.title,
    outcomeName,
    marketUrl: market.url,
    side: "buy",
    referencePricePercent1,
  };
}

/** Columns that may be shown in a position table. */
type PositionColumnKey =
  | "market"
  | "venue"
  | "classifier"
  | "price"
  | "units"
  | "orderTime"
  | "orderType"
  | "fillTime"
  | "mark"
  | "unrealized"
  | "status"
  | "result"
  | "realized";

/** Table variants whose available data differs by position lifecycle. */
type PositionTableMode = "open" | "awaiting" | "closed";

/** Human-readable labels for selectable position columns. */
const POSITION_COLUMN_LABELS: Record<PositionColumnKey, string> = {
  market: "Market",
  venue: "Venue",
  classifier: "Contract",
  price: "Price",
  units: "Units",
  orderTime: "Order time",
  orderType: "Type",
  fillTime: "Fill time",
  mark: "Mark",
  unrealized: "Unrealized P&L",
  status: "Status",
  result: "Result",
  realized: "Realized P&L",
};

/** Default visible columns, ordered for quick trading review. */
const DEFAULT_POSITION_COLUMNS: PositionColumnKey[] = [
  "market",
  "venue",
  "classifier",
  "price",
  "units",
  "orderTime",
  "orderType",
  "fillTime",
  "mark",
  "unrealized",
  "status",
  "result",
  "realized",
];

/** Columns applicable to each position lifecycle table. */
const POSITION_TABLE_COLUMNS: Record<PositionTableMode, PositionColumnKey[]> = {
  open: [
    "market",
    "venue",
    "classifier",
    "price",
    "units",
    "orderTime",
    "orderType",
    "fillTime",
    "mark",
    "unrealized",
  ],
  awaiting: [
    "market",
    "venue",
    "classifier",
    "price",
    "units",
    "orderTime",
    "orderType",
    "fillTime",
    "status",
    "mark",
  ],
  closed: [
    "market",
    "venue",
    "classifier",
    "price",
    "units",
    "orderTime",
    "orderType",
    "fillTime",
    "status",
    "result",
    "realized",
  ],
};

/**
 * Position management tables and filters.
 *
 * @param props Positions and sell callback.
 * @returns Positions surface.
 */
function Positions(props: {
  positions: PositionSummary[];
  onSell: (ticket: TradeTicket) => void;
}): React.JSX.Element {
  const [copyLabel, setCopyLabel] = useState("📋 Copy");
  const [closedCopyLabel, setClosedCopyLabel] = useState("📋 Copy");
  const [visibleColumns, setVisibleColumns] = useState<PositionColumnKey[]>(
    DEFAULT_POSITION_COLUMNS,
  );
  const [openSort, setOpenSort] = useState<PositionSort>({
    key: "fillTime",
    direction: "descending",
  });
  const [awaitingSort, setAwaitingSort] = useState<PositionSort>({
    key: "fillTime",
    direction: "descending",
  });
  const [closedSort, setClosedSort] = useState<PositionSort>({
    key: "fillTime",
    direction: "descending",
  });
  const [closedFilters, setClosedFilters] = useState<ClosedPositionFilters>(
    DEFAULT_CLOSED_POSITION_FILTERS,
  );
  const openPositions = props.positions.filter(
    (position) => position.quantity > 0 && position.marketStatus === "open",
  );
  const awaitingSettlementPositions = props.positions.filter(
    (position) => position.quantity > 0 && position.marketStatus !== "open",
  );
  const positionHistory = props.positions.filter(
    (position) => position.quantity === 0,
  );
  const closedFilterOptions = useMemo(
    () => getClosedPositionFilterOptions(positionHistory),
    [positionHistory],
  );
  const filteredPositionHistory = useMemo(
    () => filterClosedPositions(positionHistory, closedFilters),
    [closedFilters, positionHistory],
  );
  const sortedOpenPositions = useMemo(
    () => sortPositions(openPositions, openSort),
    [openPositions, openSort],
  );
  const sortedAwaitingSettlementPositions = useMemo(
    () => sortPositions(awaitingSettlementPositions, awaitingSort),
    [awaitingSettlementPositions, awaitingSort],
  );
  const sortedPositionHistory = useMemo(
    () => sortPositions(filteredPositionHistory, closedSort),
    [filteredPositionHistory, closedSort],
  );
  /** Copies every currently displayed position to a spreadsheet-ready CSV. */
  const copyPositions = async (): Promise<void> => {
    const rows = [
      ...sortedOpenPositions.map((position) =>
        createPositionCsvRow(position, "Open"),
      ),
      ...sortedAwaitingSettlementPositions.map((position) =>
        createPositionCsvRow(position, "Awaiting settlement"),
      ),
      ...sortedPositionHistory.map((position) =>
        createPositionCsvRow(
          position,
          position.settledAt === null ? "Sold" : "Settled",
        ),
      ),
    ];
    await copyCsvToClipboard(POSITIONS_CSV_HEADERS, rows, setCopyLabel);
  };
  /** Copies the currently filtered closed-position table to a spreadsheet-ready CSV. */
  const copyClosedPositions = async (): Promise<void> => {
    await copyCsvToClipboard(
      POSITIONS_CSV_HEADERS,
      sortedPositionHistory.map((position) =>
        createPositionCsvRow(
          position,
          position.settledAt === null ? "Sold" : "Settled",
        ),
      ),
      setClosedCopyLabel,
    );
  };
  /**
   * Immediately adds or removes one optional position column.
   *
   * @param column Column whose visibility changed.
   * @returns Nothing.
   */
  const toggleColumn = (column: PositionColumnKey): void => {
    setVisibleColumns((current) =>
      current.includes(column)
        ? current.filter((currentColumn) => currentColumn !== column)
        : [...current, column],
    );
  };
  return (
    <section>
      <div className="section-heading">
        <div><p className="eyebrow">Portfolio</p><h2>Open positions</h2></div>
        <div className="header-actions">
          <PositionColumnPicker
            visibleColumns={visibleColumns}
            onToggle={toggleColumn}
          />
          <button className="secondary compact" onClick={() => void copyPositions()} type="button">
            {copyLabel}
          </button>
        </div>
      </div>
      <div className="table-wrap surface">
        {openPositions.length === 0 ? (
          <div className="empty-state">Your open positions will appear here.</div>
        ) : (
          <PositionTable
            mode="open"
            positions={sortedOpenPositions}
            sort={openSort}
            onSort={setOpenSort}
            visibleColumns={visibleColumns}
            onSell={props.onSell}
          />
        )}
      </div>
      {awaitingSettlementPositions.length > 0 && (
        <>
          <div className="section-heading history-heading">
            <div><p className="eyebrow">Market closed</p><h2>Awaiting settlement</h2></div>
          </div>
          <div className="table-wrap surface">
            <PositionTable
              mode="awaiting"
              positions={sortedAwaitingSettlementPositions}
              sort={awaitingSort}
              onSort={setAwaitingSort}
              visibleColumns={visibleColumns}
            />
          </div>
        </>
      )}
      {positionHistory.length > 0 && (
        <>
          <div className="section-heading history-heading">
            <div>
              <p className="eyebrow">History</p>
              <h2>Closed positions</h2>
            </div>
            <div className="header-actions">
              <span>
                {filteredPositionHistory.length} of {positionHistory.length}
              </span>
              <button
                className="secondary compact"
                onClick={() => void copyClosedPositions()}
                type="button"
              >
                {closedCopyLabel}
              </button>
            </div>
          </div>
          <div className="closed-position-filters surface">
            <label>
              Venue
              <select
                value={closedFilters.venue}
                onChange={(event) =>
                  setClosedFilters((current) => ({
                    ...current,
                    venue: event.target.value as ClosedPositionFilters["venue"],
                  }))
                }
              >
                <option value="all">All venues</option>
                {closedFilterOptions.venues.map((venue) => (
                  <option key={venue} value={venue}>{venue}</option>
                ))}
              </select>
            </label>
            <label>
              Contract
              <select
                value={closedFilters.contract}
                onChange={(event) =>
                  setClosedFilters((current) => ({
                    ...current,
                    contract: event.target.value,
                  }))
                }
              >
                <option value="all">All contracts</option>
                {closedFilterOptions.contracts.map((contract) => (
                  <option key={contract} value={contract}>{contract}</option>
                ))}
              </select>
            </label>
            <label>
              Order type
              <select
                value={closedFilters.orderType}
                onChange={(event) =>
                  setClosedFilters((current) => ({
                    ...current,
                    orderType: event.target.value,
                  }))
                }
              >
                <option value="all">All order types</option>
                {closedFilterOptions.orderTypes.map((orderType) => (
                  <option key={orderType} value={orderType}>
                    {orderType === "none" ? "Unknown" : orderType}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Price
              <select
                value={closedFilters.price}
                onChange={(event) =>
                  setClosedFilters((current) => ({
                    ...current,
                    price: event.target.value,
                  }))
                }
              >
                <option value="all">All prices</option>
                {closedFilterOptions.pricesPercent1.map((pricePercent1) => {
                  const value = serializeClosedPositionPrice(pricePercent1);
                  return (
                    <option key={value} value={value}>
                      {formatProbability(pricePercent1)}
                    </option>
                  );
                })}
              </select>
            </label>
            <label>
              Result
              <select
                value={closedFilters.profit}
                onChange={(event) =>
                  setClosedFilters((current) => ({
                    ...current,
                    profit: event.target.value as ClosedPositionFilters["profit"],
                  }))
                }
              >
                <option value="all">Profit and loss</option>
                <option value="profit">Profit</option>
                <option value="loss">Loss</option>
                <option value="break-even">Break even</option>
              </select>
            </label>
            <button
              className="secondary compact"
              onClick={() =>
                setClosedFilters(DEFAULT_CLOSED_POSITION_FILTERS)
              }
              type="button"
            >
              Clear
            </button>
          </div>
          <div className="table-wrap table-scroll-pane surface">
            {sortedPositionHistory.length === 0 ? (
              <div className="empty-state">
                No closed positions match these filters.
              </div>
            ) : (
              <PositionTable
                mode="closed"
                positions={sortedPositionHistory}
                sort={closedSort}
                onSort={setClosedSort}
                visibleColumns={visibleColumns}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Lets an investor select which data columns are visible across position tables.
 *
 * @param props Current selection and update callback.
 * @returns Compact column-selection controller.
 */
function PositionColumnPicker(props: {
  visibleColumns: PositionColumnKey[];
  onToggle: (column: PositionColumnKey) => void;
}): React.JSX.Element {
  return (
    <details className="position-column-picker">
      <summary>Columns</summary>
      <div className="position-column-picker-menu">
        {DEFAULT_POSITION_COLUMNS.map((column) => (
          <label key={column}>
            <input
              type="checkbox"
              checked={props.visibleColumns.includes(column)}
              onChange={() => props.onToggle(column)}
            />
            {POSITION_COLUMN_LABELS[column]}
          </label>
        ))}
      </div>
    </details>
  );
}

/**
 * Renders one dense position table for a single lifecycle state.
 *
 * @param props Table rows, selected columns, sort state, and optional sell action.
 * @returns Position table.
 */
function PositionTable(props: {
  mode: PositionTableMode;
  positions: PositionSummary[];
  sort: PositionSort;
  onSort: (sort: PositionSort) => void;
  visibleColumns: PositionColumnKey[];
  onSell?: (ticket: TradeTicket) => void;
}): React.JSX.Element {
  const columns = POSITION_TABLE_COLUMNS[props.mode].filter((column) =>
    props.visibleColumns.includes(column),
  );
  return (
    <table className="position-table">
      <thead>
        <tr>
          {columns.map((column) => (
            <SortablePositionHeader
              key={column}
              label={getPositionColumnHeaderLabel(column)}
              sortKey={getPositionColumnSortKey(column)}
              sort={props.sort}
              onSort={props.onSort}
              column={column}
            />
          ))}
          {props.mode === "open" && <th aria-label="Position actions" />}
        </tr>
      </thead>
      <tbody>
        {props.positions.map((position) => {
          const sellDisabled = position.availableQuantity <= 0;
          return (
            <tr key={position.id}>
              {columns.map((column) => (
                <td key={column} className={`position-column-${column}`}>
                  {renderPositionCell(position, column, props.mode)}
                </td>
              ))}
              {props.mode === "open" && (
                <td>
                  <button
                    className="secondary compact"
                    disabled={sellDisabled}
                    onClick={() => {
                      if (props.onSell === undefined) return;
                      props.onSell({
                        venue: position.venue,
                        marketId: position.marketId,
                        outcomeId: position.outcomeId,
                        marketTitle: position.marketTitle,
                        outcomeName: position.outcomeName,
                        marketUrl: position.marketUrl,
                        side: "sell",
                        maxQuantity: position.availableQuantity,
                        referencePricePercent1: position.markPricePercent1,
                      });
                    }}
                  >
                    {sellDisabled ? "Closed" : "Sell"}
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Returns the compact, optionally stacked label for a table column.
 *
 * @param column Position column.
 * @returns Visible header label.
 */
function getPositionColumnHeaderLabel(column: PositionColumnKey): string {
  switch (column) {
    case "orderTime":
      return "Order\ntime";
    case "fillTime":
      return "Fill\ntime";
    case "unrealized":
      return "Unrealized\nP&L";
    case "realized":
      return "Realized\nP&L";
    default:
      return POSITION_COLUMN_LABELS[column];
  }
}

/**
 * Maps a displayed column to its associated sorting value.
 *
 * @param column Position column.
 * @returns Sort key for the column.
 */
function getPositionColumnSortKey(column: PositionColumnKey): PositionSortKey {
  return column;
}

/**
 * Renders a cell appropriate for the table lifecycle and selected column.
 *
 * @param position Position row.
 * @param column Selected column.
 * @param mode Position-table lifecycle.
 * @returns Position cell content.
 */
function renderPositionCell(
  position: PositionSummary,
  column: PositionColumnKey,
  mode: PositionTableMode,
): React.JSX.Element | string {
  switch (column) {
    case "market":
      return <a href={position.marketUrl} target="_blank" rel="noreferrer">{position.marketTitle}</a>;
    case "venue":
      return <span className={`venue ${position.venue}`}>{position.venue}</span>;
    case "classifier":
      return position.outcomeName;
    case "price":
      return formatProbability(position.averageAcquisitionPricePercent1);
    case "units":
      return getPositionDisplayUnits(position).toFixed(2);
    case "orderTime":
      return formatOptionalDateTime(position.orderPlacedAt);
    case "orderType":
      return position.orderType ?? "—";
    case "fillTime":
      return formatOptionalDateTime(position.filledAt);
    case "mark":
      return (
        <>
          {formatProbability(position.markPricePercent1)}
          {mode === "open" && position.markIsStale && <small>Stale mark</small>}
          {mode === "awaiting" && position.markPricePercent1 !== null && <small>Last executable mark</small>}
        </>
      );
    case "unrealized":
      return <span className={position.unrealizedProfitDollars >= 0 ? "positive" : "negative"}>{formatSignedMoney(position.unrealizedProfitDollars)}</span>;
    case "status":
      return mode === "awaiting" ? (
        <><span className="position-status pending">Pending</span><small>Waiting for the venue’s final winner</small></>
      ) : (
        <><span className={`position-status ${position.settledAt === null ? "sold" : "settled"}`}>{position.settledAt === null ? "Sold" : "Settled"}</span>{position.settledAt !== null && <small>{formatDateTime(position.settledAt)}</small>}</>
      );
    case "result":
      return <PositionResult position={position} />;
    case "realized":
      return <span className={position.realizedProfitDollars >= 0 ? "positive" : "negative"}>{formatSignedMoney(position.realizedProfitDollars)}</span>;
  }
}

/**
 * Displays the settled financial result with a compact, color-coded badge.
 *
 * @param props Closed position result.
 * @returns Win, loss, or break-even badge.
 */
function PositionResult(props: { position: PositionSummary }): React.JSX.Element {
  const result = props.position.realizedProfitDollars;
  const label = result > 0 ? "Win" : result < 0 ? "Loss" : "Even";
  const tone = result > 0 ? "win" : result < 0 ? "loss" : "even";
  return <span className={`position-result ${tone}`}>{label}</span>;
}

/**
 * Sortable position-table column header.
 *
 * @param props Header label, key, current selection, and update callback.
 * @returns Accessible column header button.
 */
function SortablePositionHeader(props: {
  label: string;
  sortKey: PositionSortKey;
  sort: PositionSort;
  onSort: (sort: PositionSort) => void;
  column?: PositionColumnKey;
}): React.JSX.Element {
  const selected = props.sort.key === props.sortKey;
  return (
    <th
      aria-sort={selected ? props.sort.direction : "none"}
      className={props.column === undefined ? undefined : `position-column-${props.column}`}
    >
      <button
        className={`sort-header${selected ? " active" : ""}`}
        type="button"
        onClick={() =>
          props.onSort(advancePositionSort(props.sort, props.sortKey))
        }
      >
        <span>{props.label}</span>
        <span aria-hidden="true" className="sort-indicator">
          {selected
            ? props.sort.direction === "ascending"
              ? "↑"
              : "↓"
            : "↕"}
        </span>
      </button>
    </th>
  );
}

/**
 * Broker-style order lifecycle table with cancellation controls.
 *
 * @param props Wallet and refresh callbacks.
 * @returns Order history surface.
 */
function Orders(props: {
  walletId: string;
  automaticRefreshActive: boolean;
  onNotice: (message: string) => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [filter, setFilter] = useState<"open" | "closed" | "all">("all");
  const [loading, setLoading] = useState(true);
  const [copyLabel, setCopyLabel] = useState("📋 Copy");
  const refresh = useCallback(async (): Promise<void> => {
    try {
      setOrders((await api.orders(props.walletId, filter)).orders);
    } catch (error) {
      props.onNotice(readError(error));
    } finally {
      setLoading(false);
    }
  }, [filter, props.onNotice, props.walletId]);
  useEffect(() => {
    if (!props.automaticRefreshActive) return;
    void refresh();
    const interval = window.setInterval(
      () => void refresh(),
      filter === "open" ? 10_000 : 60_000,
    );
    return () => {
      window.clearInterval(interval);
    };
  }, [props.automaticRefreshActive, refresh]);
  const cancel = async (order: OrderSummary): Promise<void> => {
    try {
      await api.cancelOrder(order.id);
      props.onNotice(`Canceled ${order.clientOrderId}.`);
      await refresh();
      props.onChanged();
    } catch (error) {
      props.onNotice(readError(error));
    }
  };
  /** Copies the currently displayed orders to a spreadsheet-ready CSV. */
  const copyOrders = async (): Promise<void> => {
    await copyCsvToClipboard(
      ORDERS_CSV_HEADERS,
      orders.map(createOrderCsvRow),
      setCopyLabel,
    );
  };
  return (
    <section>
      <div className="section-heading">
        <div><p className="eyebrow">Execution</p><h2>Orders</h2></div>
        <div className="header-actions">
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
            aria-label="Order status"
          >
            <option value="all">All orders</option>
            <option value="open">Open orders</option>
            <option value="closed">Closed orders</option>
          </select>
          <button className="secondary compact" onClick={() => void copyOrders()} type="button">
            {copyLabel}
          </button>
        </div>
      </div>
      <div className="table-wrap table-scroll-pane orders-scroll-pane surface">
        {loading ? (
          <div className="empty-state">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="empty-state">No orders match this filter.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Market</th><th>Order</th><th>Status</th><th>Filled</th>
                <th>Average</th><th>Times</th><th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const open = ["accepted", "partially_filled"].includes(order.status);
                const cancellationMessage = getOrderCancellationMessage(order);
                return (
                  <tr key={order.id}>
                    <td><a href={order.marketUrl} target="_blank" rel="noreferrer">{order.marketTitle}</a><small>{order.venue} · {order.outcomeName}</small></td>
                    <td>
                      {order.side} {order.type}
                      <small>
                        {order.limitPricePercent1 === null
                          ? order.timeInForce.toUpperCase()
                          : `${formatProbability(order.limitPricePercent1)} · ${order.timeInForce.toUpperCase()}`}
                      </small>
                    </td>
                    <td>
                      <span className={`order-status ${order.status}`}>{order.status.replace("_", " ")}</span>
                      {cancellationMessage !== null && <small>{cancellationMessage}</small>}
                    </td>
                    <td>{order.filledQuantity.toFixed(2)} / {order.quantity.toFixed(2)}</td>
                    <td>{formatProbability(order.averageFillPricePercent1)}</td>
                    <td className="order-timestamps">
                      <small>Submitted {formatDateTime(order.createdAt)}</small>
                      <small>Updated {formatDateTime(order.updatedAt)}</small>
                    </td>
                    <td>
                      {open && (
                        <button
                          className="danger-text"
                          onClick={() => void cancel(order)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

/**
 * Produces a concise cancellation explanation for current and legacy orders.
 *
 * @param order Persisted order shown in the history table.
 * @returns Cancellation explanation, or null for non-canceled orders.
 */
function getOrderCancellationMessage(order: OrderSummary): string | null {
  if (order.status !== "canceled") return null;
  if (order.rejectionMessage !== null) return order.rejectionMessage;
  if (order.timeInForce !== "ioc") return "This order was canceled before it fully executed.";
  return order.filledQuantity > 0
    ? "IOC filled immediately available shares; the remainder was canceled."
    : "IOC found no immediately executable shares at its limit price.";
}

/**
 * API key management surface.
 *
 * @param props Selected wallet, available wallets, and notice callback.
 * @returns API key section.
 */
function ApiKeys(props: {
  wallet: WalletListItem | null;
  wallets: WalletListItem[];
  onNotice: (message: string) => void;
}): React.JSX.Element {
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [updateStatuses, setUpdateStatuses] = useState<
    Record<string, "saving" | "saved" | undefined>
  >({});
  const refresh = useCallback(
    () =>
      api
        .apiKeys()
        .then(setKeys)
        .catch((error: unknown) => props.onNotice(readError(error))),
    [props.onNotice],
  );
  useEffect(() => void refresh(), [refresh]);
  const create = async (): Promise<void> => {
    const name = window.prompt("API key name", "Trading script");
    if (
      name === null ||
      name.trim() === "" ||
      props.wallet === null
    ) return;
    try {
      const result = await api.createApiKey(name, props.wallet.id);
      setRevealedKey(result.key);
      refresh();
    } catch (error) {
      props.onNotice(readError(error));
    }
  };
  const updateKey = async (
    key: ApiKeySummary,
    input: UpdateApiKeyRequest,
  ): Promise<void> => {
    setUpdateStatuses((current) => ({ ...current, [key.id]: "saving" }));
    try {
      const updatedKey = await api.updateApiKey(key.id, input);
      setKeys((current) =>
        current.map((candidate) =>
          candidate.id === updatedKey.id ? updatedKey : candidate
        ),
      );
      setUpdateStatuses((current) => ({ ...current, [key.id]: "saved" }));
    } catch (error) {
      setUpdateStatuses((current) => ({ ...current, [key.id]: undefined }));
      props.onNotice(readError(error));
    }
  };
  return (
    <section>
      <div className="section-heading">
        <div><p className="eyebrow">Automation</p><h2>API keys</h2></div>
        <div className="header-actions">
          <a className="secondary docs-action" href="/docs">Read API docs</a>
          <button
            className="primary"
            onClick={() => void create()}
            disabled={props.wallet === null}
          >
            Create key
          </button>
        </div>
      </div>
      {revealedKey !== null && (
        <div className="key-reveal surface">
          <strong>Copy this key now</strong>
          <code>{revealedKey}</code>
          <button className="secondary" onClick={() => void navigator.clipboard.writeText(revealedKey)}>Copy</button>
        </div>
      )}
      <div className="table-wrap surface">
        {keys.length === 0 ? <div className="empty-state">No API keys yet.</div> : (
          <table><thead><tr><th>Name</th><th>Prefix</th><th>Default wallet</th><th>Access</th><th>Level</th><th>Last used</th><th /></tr></thead>
            <tbody>{keys.map((key) => <tr key={key.id}>
              <td>{key.name}</td><td><code>{key.prefix}…</code></td>
              <td>
                <div className="api-key-setting">
                  <select
                    aria-label={`Default wallet for ${key.name}`}
                    value={key.defaultWalletId}
                    disabled={updateStatuses[key.id] === "saving"}
                    onChange={(event) =>
                      void updateKey(key, {
                        defaultWalletId: event.target.value,
                      })
                    }
                  >
                    {props.wallets.map((wallet) => (
                      <option key={wallet.id} value={wallet.id}>
                        {wallet.name}
                      </option>
                    ))}
                  </select>
                  <small>Used when a request omits its wallet.</small>
                </div>
              </td>
              <td>
                <div className="api-key-setting">
                  <select
                    aria-label={`Wallet access for ${key.name}`}
                    value={key.access}
                    disabled={updateStatuses[key.id] === "saving"}
                    onChange={(event) =>
                      void updateKey(
                        key,
                        {
                          access: event.target.value as "wallet" | "all",
                        },
                      )
                    }
                  >
                    <option value="wallet">{key.defaultWalletName}</option>
                    <option value="all">All wallets</option>
                  </select>
                  {updateStatuses[key.id] !== undefined && (
                    <small className="access-status" role="status">
                      {updateStatuses[key.id] === "saving"
                        ? "Saving…"
                        : "Saved"}
                    </small>
                  )}
                </div>
              </td>
              <td>
                <div className="api-key-setting">
                  <select
                    aria-label={`Authority level for ${key.name}`}
                    value={key.level}
                    disabled={updateStatuses[key.id] === "saving"}
                    onChange={(event) =>
                      void updateKey(key, {
                        level: event.target.value as "wallet" | "account",
                      })
                    }
                  >
                    <option value="wallet">Trading</option>
                    <option value="account">Account management</option>
                  </select>
                  <small>
                    {key.level === "account"
                      ? "Can manage paper accounts and API keys."
                      : "Cannot manage accounts or API keys."}
                  </small>
                </div>
              </td>
              <td>{key.lastUsedAt === null ? "Never" : formatDate(key.lastUsedAt)}</td>
              <td><button className="danger-text" onClick={() => void api.revokeApiKey(key.id).then(refresh)}>Revoke</button></td>
            </tr>)}</tbody>
          </table>
        )}
      </div>
    </section>
  );
}

/**
 * Market order dialog.
 *
 * @param props Ticket, wallet, and completion callbacks.
 * @returns Modal dialog.
 */
function TradeDialog(props: {
  ticket: TradeTicket;
  walletId: string;
  automaticRefreshActive: boolean;
  onClose: () => void;
  onFilled: (message: string) => void;
}): React.JSX.Element {
  const [quantity, setQuantity] = useState(1);
  const [orderType, setOrderType] = useState<"market" | "limit">("market");
  const [timeInForce, setTimeInForce] = useState<"gtc" | "ioc" | "fok">("gtc");
  const [limitPricePercent100, setLimitPricePercent100] = useState(
    (props.ticket.referencePricePercent1 ?? 0.5) * 100,
  );
  const [livePricePercent1, setLivePricePercent1] = useState(
    props.ticket.referencePricePercent1 ?? null,
  );
  const [livePriceUpdatedAtMilliseconds, setLivePriceUpdatedAtMilliseconds] =
    useState<number | null>(null);
  const [livePriceError, setLivePriceError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!props.automaticRefreshActive) return;
    let active = true;
    let inFlight = false;
    const refreshLivePrice = async (): Promise<void> => {
      if (inFlight) {
        return;
      }
      inFlight = true;
      try {
        const book = await api.orderBook(
          props.ticket.venue,
          props.ticket.marketId,
          props.ticket.outcomeId,
        );
        if (!active) return;
        const bestLevel =
          props.ticket.side === "buy" ? book.asks[0] : book.bids[0];
        setLivePricePercent1(bestLevel?.pricePercent1 ?? null);
        const observedAtMilliseconds = Date.parse(book.observedAt);
        setLivePriceUpdatedAtMilliseconds(
          Number.isFinite(observedAtMilliseconds)
            ? observedAtMilliseconds
            : Date.now(),
        );
        setLivePriceError(null);
      } catch {
        if (active) {
          setLivePriceError("Live price temporarily unavailable.");
        }
      } finally {
        inFlight = false;
      }
    };
    void refreshLivePrice();
    const interval = window.setInterval(
      () => void refreshLivePrice(),
      ACTIVE_MARKET_REFRESH_INTERVAL_MILLISECONDS,
    );
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [props.automaticRefreshActive, props.ticket]);
  const request = useMemo<PlaceOrderRequest>(() => ({
    walletId: props.walletId,
    venue: props.ticket.venue,
    marketId: props.ticket.marketId,
    outcomeId: props.ticket.outcomeId,
    side: props.ticket.side,
    quantity,
    type: orderType,
    ...(orderType === "limit"
      ? {
          timeInForce,
          limitPricePercent1: limitPricePercent100 / 100,
        }
      : {}),
  }), [
    limitPricePercent100,
    orderType,
    props.ticket,
    props.walletId,
    quantity,
    timeInForce,
  ]);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const order = await api.placeOrder(request);
      props.onFilled(
        ["accepted", "partially_filled"].includes(order.status)
          ? `Placed ${order.side} limit order; ${order.filledQuantity.toFixed(2)} of ${order.quantity.toFixed(2)} contracts filled.`
          : `${order.side === "buy" ? "Bought" : "Sold"} ${order.filledQuantity.toFixed(2)} ${order.outcomeName}${order.averageFillPricePercent1 === null ? "" : ` at ${(order.averageFillPricePercent1 * 100).toFixed(1)}¢`}.`,
      );
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
      <dialog open className="trade-dialog surface">
        <button className="modal-close" onClick={props.onClose} aria-label="Close">×</button>
        <span className={`venue ${props.ticket.venue}`}>{props.ticket.venue}</span>
        <h2>{props.ticket.side === "buy" ? "Buy" : "Sell"} {props.ticket.outcomeName}</h2>
        <p>{props.ticket.marketTitle}</p>
        <div className="live-trade-price">
          <span>Live {props.ticket.side === "buy" ? "ask" : "bid"}</span>
          <strong>{formatProbability(livePricePercent1)}</strong>
          <RelativeUpdatedAt
            updatedAtMilliseconds={livePriceUpdatedAtMilliseconds}
          />
          {livePriceError !== null && <small>{livePriceError}</small>}
        </div>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Order type
            <select
              value={orderType}
              onChange={(event) => setOrderType(event.target.value as typeof orderType)}
            >
              <option value="market">Market</option>
              <option value="limit">Limit</option>
            </select>
          </label>
          <label>Contracts<input type="number" min="0.01" step="0.01" max={props.ticket.maxQuantity} value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} /></label>
          {orderType === "limit" && (
            <div className="order-options">
              <label>
                Limit price (¢)
                <input
                  type="number"
                  min="0.1"
                  max="99.9"
                  step="0.1"
                  value={limitPricePercent100}
                  onChange={(event) => setLimitPricePercent100(Number(event.target.value))}
                  required
                />
              </label>
              <label>
                Time in force
                <select
                  value={timeInForce}
                  onChange={(event) => setTimeInForce(event.target.value as typeof timeInForce)}
                >
                  <option value="gtc">Good until canceled</option>
                  <option value="ioc">Immediate or cancel</option>
                  <option value="fok">Fill or kill</option>
                </select>
              </label>
            </div>
          )}
          <p className="muted">
            {orderType === "market"
              ? "The order walks live L2 liquidity and rejects unless it fills within your wallet’s slippage limit."
              : "Up to 30% of eligible displayed depth can fill now; any GTC remainder stays open for later book observations."}
          </p>
          {error !== null && <div className="notice error">{error}</div>}
          <button className="primary wide" disabled={busy}>
            {busy
              ? "Submitting…"
              : `${props.ticket.side === "buy" ? "Buy" : "Sell"} ${orderType}`}
          </button>
        </form>
      </dialog>
    </div>
  );
}

/**
 * Safely turns an unknown error into display text.
 *
 * @param error Unknown caught value.
 * @returns Readable message.
 */
function readError(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

/**
 * Formats an optional dollar amount.
 *
 * @param value Dollar value.
 * @returns USD display string.
 */
function formatMoney(value: number | null | undefined): string {
  return value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

/**
 * Formats a signed dollar amount.
 *
 * @param value Dollar value.
 * @returns Signed USD display string.
 */
function formatSignedMoney(value: number | undefined): string {
  if (value === undefined) return "—";
  return `${value >= 0 ? "+" : ""}${formatMoney(value)}`;
}

/**
 * Formats a percentage on a scale of 100 for the dashboard.
 *
 * @param valuePercent100 Percentage value, or null when no rate exists.
 * @returns Compact percentage label.
 */
function formatPercent(valuePercent100: number | null): string {
  return valuePercent100 === null ? "—" : `${valuePercent100.toFixed(0)}%`;
}

/**
 * Formats a signed percentage on the scale-100 convention.
 *
 * @param valuePercent100 Percentage value, or null when unavailable.
 * @returns Signed percentage label.
 */
function formatSignedPercent(valuePercent100: number | null): string {
  if (valuePercent100 === null) return "—";
  return `${valuePercent100 >= 0 ? "+" : "−"}${Math.abs(valuePercent100).toFixed(1)}%`;
}

/**
 * Formats a peak-to-trough loss percentage.
 *
 * @param valuePercent100 Positive drawdown magnitude on a scale of 100.
 * @returns Negative drawdown label.
 */
function formatDrawdownPercent(valuePercent100: number | undefined): string {
  if (valuePercent100 === undefined) return "—";
  return valuePercent100 === 0 ? "0.0%" : `−${valuePercent100.toFixed(1)}%`;
}

/**
 * Formats a peak-to-trough loss in dollars.
 *
 * @param valueDollars Positive drawdown magnitude in dollars.
 * @returns Negative dollar loss label.
 */
function formatDrawdownMoney(valueDollars: number | undefined): string {
  if (valueDollars === undefined) return "—";
  return valueDollars === 0 ? formatMoney(0) : formatSignedMoney(-valueDollars);
}

/**
 * Formats an optional ratio to two decimal places.
 *
 * @param value Ratio value, or null when it needs more observations.
 * @returns Ratio label.
 */
function formatRatio(value: number | null): string {
  return value === null ? "—" : value.toFixed(2);
}

/**
 * Formats a signed movement expressed as percentage points on a scale of 100.
 *
 * @param valuePercent100 Signed percentage-point movement.
 * @returns Compact movement label.
 */
function formatSignedPoints(valuePercent100: number): string {
  const sign = valuePercent100 >= 0 ? "+" : "−";
  return `${sign}${Math.abs(valuePercent100).toFixed(1)} pts`;
}

/**
 * Formats a probability as cents.
 *
 * @param value Probability on scale 1.
 * @returns Cents display string.
 */
function formatProbability(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}¢`;
}

/**
 * Formats an ISO date for a compact UI.
 *
 * @param value ISO date.
 * @returns Localized date.
 */
function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

/**
 * Formats a full venue timestamp in the application's display timezone.
 *
 * @param value ISO timestamp.
 * @returns Full localized timestamp.
 */
function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    timeZone: "America/New_York",
  });
}

/**
 * Formats an optional venue timestamp in the application's display timezone.
 *
 * @param value ISO timestamp or null.
 * @returns Full localized timestamp or an em dash.
 */
function formatOptionalDateTime(value: string | null): string {
  return value === null ? "—" : formatDateTime(value);
}

/**
 * Creates a unified spreadsheet row for a position.
 *
 * @param position Position summary to export.
 * @param status Display status for the current positions view.
 * @returns CSV-compatible position values.
 */
function createPositionCsvRow(
  position: PositionSummary,
  status: string,
): CsvRow {
  return [
    position.marketTitle,
    position.venue,
    position.outcomeName,
    formatProbability(position.averageAcquisitionPricePercent1),
    getPositionDisplayUnits(position).toFixed(2),
    formatOptionalDateTime(position.orderPlacedAt),
    position.orderType ?? "—",
    formatOptionalDateTime(position.filledAt),
    status,
    formatProbability(position.markPricePercent1),
    formatSignedMoney(position.unrealizedProfitDollars),
    formatProbability(position.settlementPricePercent1),
    formatSignedMoney(position.realizedProfitDollars),
  ];
}

/**
 * Creates a spreadsheet row for an order.
 *
 * @param order Order summary to export.
 * @returns CSV-compatible order values.
 */
function createOrderCsvRow(order: OrderSummary): CsvRow {
  return [
    order.marketTitle,
    order.venue,
    order.outcomeName,
    order.side,
    order.type,
    order.timeInForce.toUpperCase(),
    order.status.replace("_", " "),
    order.filledQuantity.toFixed(2),
    order.quantity.toFixed(2),
    formatProbability(order.averageFillPricePercent1),
    formatProbability(order.limitPricePercent1),
    formatMoney(order.feeDollars),
    formatMoney(order.grossDollars),
    formatDateTime(order.createdAt),
    formatDateTime(order.updatedAt),
    order.clientOrderId,
  ];
}

/**
 * Copies a table to the clipboard as RFC 4180-compatible CSV and briefly confirms success.
 *
 * @param headers CSV column labels.
 * @param rows CSV table rows.
 * @param setLabel Updates the copy-control label.
 * @returns Nothing.
 */
async function copyCsvToClipboard(
  headers: readonly string[],
  rows: readonly CsvRow[],
  setLabel: (label: string) => void,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(serializeCsv([headers, ...rows]));
    setLabel("Copied!");
    window.setTimeout(() => setLabel("📋 Copy"), 1_800);
  } catch {
    setLabel("Copy failed");
    window.setTimeout(() => setLabel("📋 Copy"), 1_800);
  }
}

/**
 * Serializes a table to escaped CSV rows for spreadsheet pasting.
 *
 * @param rows Table data to serialize.
 * @returns RFC 4180-compatible CSV text.
 */
function serializeCsv(rows: readonly CsvRow[]): string {
  return rows
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\r\n");
}

/**
 * Escapes one CSV cell while preserving its literal display value.
 *
 * @param value Cell value to escape.
 * @returns Escaped CSV cell.
 */
function escapeCsvCell(value: CsvCell): string {
  const text = value === null ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}
